<template>
  <DialogLayout class="flow-record-todo"
                title="待办处理"
                v-loading="loading"
                element-loading-text="数据加载中，请稍等...">
    <template v-if="recordInfo">
      <div class="todo-main">

        <AppScrollbar class="todo-scroll">
          <div class="todo-content">

            <!-- 流程信息卡片 -->
            <div class="todo-section">
              <div class="section-header">
                <span class="section-title theme-text">{{ approvalInfo.processDefinitionName }}</span>
                <div class="section-header-tags">
                  <el-tag v-if="approvalInfo.ended" size="mini" type="info">已结束</el-tag>
                  <el-tag v-else size="mini" type="success">启用中</el-tag>
                  <el-tag v-if="approvalInfo.delegated" size="mini" type="warning">委派中</el-tag>
                </div>
              </div>
              <div class="section-body section-body-info">
                <div v-if="approvalInfo.flowNo" class="info-row">
                  <span class="info-label">流水号</span>
                  <span class="info-value">{{ approvalInfo.flowNo || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">发起人</span>
                  <span class="info-value">{{ approvalInfo.startUser || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">提交时间</span>
                  <span class="info-value">{{ approvalInfo.startTime || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">任务节点</span>
                  <span class="info-value">{{ approvalInfo.taskName || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">审批状态</span>
                  <span class="info-value">
                    <el-tag v-if="approvalInfo.bizStatus" size="mini" :type="bizStatusTagType">{{ approvalInfo.bizStatus.name }}</el-tag>
                    <span v-else>-</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- 审批详情卡片 -->
            <div class="todo-section">
              <div class="section-header">
                <span class="section-title theme-text">审批详情</span>
                <el-button type="text" size="mini" @click="formExpanded = !formExpanded">
                  <i :class="formExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  {{ formExpanded ? '折叠' : '展开' }}
                </el-button>
              </div>
              <el-collapse-transition>
                <div v-show="formExpanded" class="section-body">
                  <FlowCommonApprovalForm :form-info="formInfo"></FlowCommonApprovalForm>
                </div>
              </el-collapse-transition>
            </div>

            <!-- 流程记录卡片 -->
            <div class="todo-section">
              <div class="section-header">
                <span class="section-title theme-text">流程记录</span>
              </div>
              <div class="section-body section-body-history">
                <FlowCommonHistory v-if="historyInfo"
                                   :history-info="historyInfo"
                                   :timeline="true"></FlowCommonHistory>
                <div v-else class="empty-hint">暂无流程记录</div>
              </div>
            </div>

          </div>
        </AppScrollbar>

        <!-- 底部操作栏 -->
        <ActionGroup class="todo-actions">
          <template v-if="!approvalInfo.ended">
            <ActionItem v-if="approvalInfo.canTransfer">
              <el-button type="text" size="small" icon="el-icon-position" @click="handleTransfer">转办</el-button>
            </ActionItem>
            <ActionItem v-if="approvalInfo.canDelegate">
              <el-button type="text" size="small" icon="el-icon-user" @click="handleDelegate">委派</el-button>
            </ActionItem>
            <ActionItem v-if="approvalInfo.canAddMulti">
              <el-button type="text" size="small" icon="el-icon-circle-plus-outline" @click="handleParallelMulti">加签</el-button>
            </ActionItem>
            <ActionItem v-if="approvalInfo.canComment">
              <el-button type="text" size="small" icon="el-icon-chat-line-round" @click="handleComment">评论</el-button>
            </ActionItem>
            <!-- 委派状态只显示提交 -->
            <template v-if="approvalInfo.delegated">
              <ActionItem position="right">
                <el-button type="primary" size="mini" plain icon="el-icon-s-promotion" @click="handleResolve">提交</el-button>
              </ActionItem>
            </template>
            <template v-else>
              <template v-if="approvalInfo.isTransact">
                <ActionItem position="right">
                  <el-button type="success" size="mini" plain icon="el-icon-s-check" @click="handleTransact">办理</el-button>
                </ActionItem>
              </template>
              <template v-else>
                <ActionItem position="right">
                  <el-button type="success" size="mini" plain icon="el-icon-success" @click="handleAgree">同意</el-button>
                </ActionItem>
                <ActionItem position="right">
                  <el-button type="danger" size="mini" plain icon="el-icon-error" @click="handleReject">拒绝</el-button>
                </ActionItem>
              </template>
            </template>
          </template>
          <template slot="empty">
            <span v-if="approvalInfo.ended" class="flow-ended-text"><i class="el-icon-circle-check"></i><span>流程已结束，相关审批任务已完成</span></span>
            <span v-else class="flow-ended-text"><i class="el-icon-warning-outline"></i><span>暂无可用操作</span></span>
          </template>
        </ActionGroup>

      </div>
    </template>
    <template v-else-if="!loading">
      <Hint type="error">
        <div>流程数据加载失败</div>
        <el-button size="mini" type="text" @click="loadDetailInfo">重新加载</el-button>
      </Hint>
    </template>

    <!-- 转办弹窗 -->
    <DataGridDialog title="流程转办" :visible.sync="transferDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalForm ref="transferForm">
          <template slot="prepend">
            <el-form-item label="转交人员">
              <div class="member-select-area">
                <el-button size="mini" icon="el-icon-plus" round @click="selectTransferMember"></el-button>
                <template v-if="transferDialog.transferUser.length">
                  <member-items v-model="transferDialog.transferUser" :single="true" class="transfer-member-items"></member-items>
                </template>
                <template v-else>
                  <span class="transfer-member-placeholder">请设置要转交给谁</span>
                </template>
              </div>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="transferDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" :disabled="transferDialog.transferUser.length === 0"
                   :loading="transferDialog.submitting" @click="confirmTransfer">确定转办</el-button>
      </div>
    </DataGridDialog>
    <member-picker title="请选择转交人员" ref="transferMemberPicker"
                   :selected="transferDialog.transferUser" @ok="onTransferMemberSelected"/>

    <!-- 委派弹窗 -->
    <DataGridDialog title="流程委派" :visible.sync="delegateDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalForm ref="delegateForm">
          <template slot="prepend">
            <el-form-item label="委派人员">
              <div class="member-select-area">
                <el-button size="mini" icon="el-icon-plus" round @click="selectDelegateMember"></el-button>
                <template v-if="delegateDialog.delegateUser.length">
                  <member-items v-model="delegateDialog.delegateUser" :single="true" class="delegate-member-items"></member-items>
                </template>
                <template v-else>
                  <span class="delegate-member-placeholder">请选择委派给谁</span>
                </template>
              </div>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="delegateDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" :disabled="delegateDialog.delegateUser.length === 0"
                   :loading="delegateDialog.submitting" @click="confirmDelegate">确定委派</el-button>
      </div>
    </DataGridDialog>
    <member-picker title="请选择委派人员" ref="delegateMemberPicker"
                   :selected="delegateDialog.delegateUser" @ok="onDelegateMemberSelected"/>

    <!-- 加签弹窗 -->
    <DataGridDialog title="流程加签" :visible.sync="parallelMultiDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalForm ref="parallelMultiForm">
          <template slot="prepend">
            <el-form-item label="加签人员">
              <div class="member-select-area">
                <el-button size="mini" icon="el-icon-plus" round @click="selectParallelMultiMember"></el-button>
                <template v-if="parallelMultiDialog.multiAddUser.length">
                  <member-items v-model="parallelMultiDialog.multiAddUser" class="parallel-multi-member-items"></member-items>
                </template>
                <template v-else>
                  <span class="parallel-multi-member-placeholder">请选择加签人员</span>
                </template>
              </div>
            </el-form-item>
          </template>
        </FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="parallelMultiDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" :disabled="parallelMultiDialog.multiAddUser.length === 0"
                   :loading="parallelMultiDialog.submitting" @click="confirmParallelMulti">确定加签</el-button>
      </div>
    </DataGridDialog>
    <member-picker title="请选择加签人员" ref="parallelMultiMemberPicker"
                   :selected="parallelMultiDialog.multiAddUser" @ok="onParallelMultiMemberSelected"/>

    <!-- 评论弹窗 -->
    <DataGridDialog title="添加评论" :visible.sync="commentDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalForm ref="commentForm" comment-label="评论内容" :comment-required="true"
                          comment-placeholder="请输入评论内容"></FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="commentDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="commentDialog.submitting" @click="confirmComment">确定评论</el-button>
      </div>
    </DataGridDialog>

    <!-- 同意弹窗 -->
    <DataGridDialog title="审批同意" :visible.sync="agreeDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalHandle ref="agreeApproval"
                            handle-type="approval"
                            :approval-info="approvalInfo"
                            :submitting="agreeDialog.submitting"
                            @cancel="agreeDialog.visible = false"
                            @submit="confirmAgree" />
      </div>
    </DataGridDialog>

    <!-- 拒绝弹窗 -->
    <DataGridDialog title="审批拒绝" :visible.sync="rejectDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalForm ref="rejectForm" comment-label="拒绝原因" :comment-required="true"
                          comment-placeholder="请输入拒绝原因"></FlowApprovalForm>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="rejectDialog.visible = false">取消</el-button>
        <el-button type="danger" size="small" :loading="rejectDialog.submitting" @click="confirmReject">确定拒绝</el-button>
      </div>
    </DataGridDialog>

    <!-- 办理弹窗 -->
    <DataGridDialog title="任务办理" :visible.sync="transactDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalHandle ref="transactApproval"
                            handle-type="transact"
                            :approval-info="approvalInfo"
                            :submitting="transactDialog.submitting"
                            @cancel="transactDialog.visible = false"
                            @submit="confirmTransact" />
      </div>
    </DataGridDialog>

    <!-- 提交弹窗（委派状态） -->
    <DataGridDialog title="提交任务" :visible.sync="resolveDialog.visible" width="500px"
                    custom-class="flow-record-todo-dialog" :close-on-click-modal="false">
      <div class="dialog-form-content">
        <FlowApprovalHandle ref="resolveApproval"
                            handle-type="resolve"
                            :approval-info="approvalInfo"
                            :submitting="resolveDialog.submitting"
                            @cancel="resolveDialog.visible = false"
                            @submit="confirmResolve" />
      </div>
    </DataGridDialog>

  </DialogLayout>
</template>

<script>
import DialogLayout from '../../../../widgets/DialogLayout'
import AppScrollbar from '../../../../elements/AppScrollbar'
import FlowCommonApprovalForm from '../../common/approval/form'
import FlowCommonHistory from '../../common/approval/history'
import FlowApprovalHandle from '../../common/form/approval-handle'
import Hint from '../../../../elements/Hint'
import DataGridDialog from '../../../../elements/grid/DataGridDialog'
import FlowApprovalForm from '../../../../elements/flow/approval/FlowApprovalForm'
import MemberPicker from '../../../../elements/flow/common/MemberPicker'
import MemberItems from '../../../../elements/flow/process/MemberItems'
import ActionGroup from '../../../../widgets/action-group/ActionGroup'
import ActionItem from '../../../../widgets/action-group/ActionItem'

export default {
  name: 'FlowRecordTodo',
  components: {
    DialogLayout,
    AppScrollbar,
    Hint,
    DataGridDialog,
    FlowApprovalForm,
    MemberPicker,
    MemberItems,
    ActionGroup,
    ActionItem,
    FlowCommonHistory,
    FlowCommonApprovalForm,
    FlowApprovalHandle
  },
  props: {
    rowData: {
      type: Object,
      required: true
    },
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      recordInfo: null,
      formExpanded: true,
      transferDialog: {
        visible: false,
        submitting: false,
        transferUser: []
      },
      delegateDialog: {
        visible: false,
        submitting: false,
        delegateUser: []
      },
      parallelMultiDialog: {
        visible: false,
        submitting: false,
        multiAddUser: []
      },
      commentDialog: {
        visible: false,
        submitting: false
      },
      agreeDialog: {
        visible: false,
        submitting: false
      },
      rejectDialog: {
        visible: false,
        submitting: false
      },
      transactDialog: {
        visible: false,
        submitting: false
      },
      resolveDialog: {
        visible: false,
        submitting: false
      }
    }
  },
  computed: {
    approvalInfo () {
      return (this.recordInfo && this.recordInfo.approval) || {}
    },
    formInfo () {
      return this.recordInfo && this.recordInfo.form
    },
    historyInfo () {
      return this.recordInfo && this.recordInfo.process
    },
    bizStatusTagType () {
      const bizStatus = this.approvalInfo && this.approvalInfo.bizStatus
      if (bizStatus) {
        switch (bizStatus.code) {
          case '1': return 'primary'
          case '2': return 'info'
          case '3': return 'danger'
          case '4': return 'success'
          default: break
        }
      }
      return 'info'
    }
  },
  methods: {
    loadDetailInfo () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/flow/record/todo/process/record',
        method: 'post',
        data: this.$qs.stringify({ id: this.rowData.id })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.recordInfo = response.data.recordInfo || {}
              break
            }
            default: {
              this.$message.warning(response.data['message'])
              break
            }
          }
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    // 转办
    handleTransfer () {
      this.transferDialog.visible = true
      this.transferDialog.submitting = false
      this.transferDialog.transferUser = []
      this.$nextTick(() => {
        if (this.$refs.transferForm) {
          this.$refs.transferForm.reset()
        }
      })
    },
    selectTransferMember () {
      this.$refs.transferMemberPicker.show()
    },
    onTransferMemberSelected (selected) {
      if (selected && selected.length > 0) {
        this.transferDialog.transferUser = [selected[0]]
      } else {
        this.transferDialog.transferUser = []
      }
    },
    confirmTransfer () {
      if (this.transferDialog.transferUser.length === 0) {
        this.$message.warning('请选择转交人员')
        return
      }
      this.$refs.transferForm.validate().then(formData => {
        this.$confirm('确定要转办此任务吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.transferDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/record/todo/process/transfer',
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data: this.$extend({
              id: this.rowData.id,
              transferUser: this.transferDialog.transferUser[0]
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '转办成功')
                  this.transferDialog.visible = false
                  this.$emit('hidePopoverAndRefresh')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.transferDialog.submitting = false
          })
        }).catch(() => {})
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 委派
    handleDelegate () {
      this.delegateDialog.visible = true
      this.delegateDialog.submitting = false
      this.delegateDialog.delegateUser = []
      this.$nextTick(() => {
        if (this.$refs.delegateForm) {
          this.$refs.delegateForm.reset()
        }
      })
    },
    selectDelegateMember () {
      this.$refs.delegateMemberPicker.show()
    },
    onDelegateMemberSelected (selected) {
      if (selected && selected.length > 0) {
        this.delegateDialog.delegateUser = [selected[0]]
      } else {
        this.delegateDialog.delegateUser = []
      }
    },
    confirmDelegate () {
      if (this.delegateDialog.delegateUser.length === 0) {
        this.$message.warning('请选择委派人员')
        return
      }
      this.$refs.delegateForm.validate().then(formData => {
        this.$confirm('确定要委派此任务吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.delegateDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/record/todo/process/delegate',
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data: this.$extend({
              id: this.rowData.id,
              delegateUser: this.delegateDialog.delegateUser[0]
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '委派成功')
                  this.delegateDialog.visible = false
                  this.$emit('hidePopoverAndRefresh')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.delegateDialog.submitting = false
          })
        }).catch(() => {})
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 加签
    handleParallelMulti () {
      this.parallelMultiDialog.visible = true
      this.parallelMultiDialog.submitting = false
      this.parallelMultiDialog.multiAddUser = []
      this.$nextTick(() => {
        if (this.$refs.parallelMultiForm) {
          this.$refs.parallelMultiForm.reset()
        }
      })
    },
    selectParallelMultiMember () {
      this.$refs.parallelMultiMemberPicker.show()
    },
    onParallelMultiMemberSelected (selected) {
      if (selected && selected.length > 0) {
        this.parallelMultiDialog.multiAddUser = selected
      } else {
        this.parallelMultiDialog.multiAddUser = []
      }
    },
    confirmParallelMulti () {
      if (this.parallelMultiDialog.multiAddUser.length === 0) {
        this.$message.warning('请选择加签人员')
        return
      }
      this.$refs.parallelMultiForm.validate().then(formData => {
        this.$confirm('确定要加签此任务吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.parallelMultiDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/record/todo/process/parallelMulti',
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data: this.$extend({
              id: this.rowData.id,
              multiAddUser: this.parallelMultiDialog.multiAddUser[0]
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '加签成功')
                  this.parallelMultiDialog.visible = false
                  this.loadDetailInfo()
                  this.$emit('reloadGridData')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.parallelMultiDialog.submitting = false
          })
        }).catch(() => {})
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 评论
    handleComment () {
      this.commentDialog.visible = true
      this.commentDialog.submitting = false
      this.$nextTick(() => {
        if (this.$refs.commentForm) {
          this.$refs.commentForm.reset()
        }
      })
    },
    confirmComment () {
      this.$refs.commentForm.validate().then(formData => {
        this.$confirm('确定要提交评论吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.commentDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/record/todo/process/comment',
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data: this.$extend({
              id: this.rowData.id
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '评论成功')
                  this.commentDialog.visible = false
                  this.loadDetailInfo()
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.commentDialog.submitting = false
          })
        }).catch(() => {})
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 同意
    handleAgree () {
      this.agreeDialog.visible = true
      this.agreeDialog.submitting = false
      this.$nextTick(() => {
        if (this.$refs.agreeApproval) {
          this.$refs.agreeApproval.reset()
        }
      })
    },
    confirmAgree (data) {
      this.$confirm('确定要同意此审批吗？', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.agreeDialog.submitting = true
        this.$ajax({
          url: '/workspace/manager/flow/record/todo/process/agree',
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          data: this.$extend({
            id: this.rowData.id
          }, data)
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                this.$message.success(response.data['message'] || '审批同意成功')
                this.agreeDialog.visible = false
                this.$emit('hidePopoverAndRefresh')
                break
              }
              default: {
                this.$message.warning(response.data['message'])
                break
              }
            }
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.agreeDialog.submitting = false
        })
      }).catch(() => {})
    },
    // 拒绝
    handleReject () {
      this.rejectDialog.visible = true
      this.rejectDialog.submitting = false
      this.$nextTick(() => {
        if (this.$refs.rejectForm) {
          this.$refs.rejectForm.reset()
        }
      })
    },
    confirmReject () {
      this.$refs.rejectForm.validate().then(formData => {
        this.$confirm('确定要拒绝此审批吗？', '确认提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.rejectDialog.submitting = true
          this.$ajax({
            url: '/workspace/manager/flow/record/todo/process/reject',
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data: this.$extend({
              id: this.rowData.id
            }, formData)
          }).then(response => {
            if (response.data) {
              switch (response.data['responseCode']) {
                case 'SUCCESS': {
                  this.$message.success(response.data['message'] || '审批拒绝成功')
                  this.rejectDialog.visible = false
                  this.$emit('hidePopoverAndRefresh')
                  break
                }
                default: {
                  this.$message.warning(response.data['message'])
                  break
                }
              }
            }
          }, () => {
            this.$message.error('请求发送失败，请稍后重试')
          }).finally(() => {
            this.rejectDialog.submitting = false
          })
        }).catch(() => {})
      }).catch(err => {
        this.$message.warning(err.message)
      })
    },
    // 办理
    handleTransact () {
      this.transactDialog.visible = true
      this.transactDialog.submitting = false
      this.$nextTick(() => {
        if (this.$refs.transactApproval) {
          this.$refs.transactApproval.reset()
        }
      })
    },
    confirmTransact (data) {
      this.$confirm('确定要办理此任务吗？', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.transactDialog.submitting = true
        this.$ajax({
          url: '/workspace/manager/flow/record/todo/process/transact',
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          data: this.$extend({
            id: this.rowData.id
          }, data)
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                this.$message.success(response.data['message'] || '办理成功')
                this.transactDialog.visible = false
                this.$emit('hidePopoverAndRefresh')
                break
              }
              default: {
                this.$message.warning(response.data['message'])
                break
              }
            }
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.transactDialog.submitting = false
        })
      }).catch(() => {})
    },
    // 提交（委派状态）
    handleResolve () {
      this.resolveDialog.visible = true
      this.resolveDialog.submitting = false
      this.$nextTick(() => {
        if (this.$refs.resolveApproval) {
          this.$refs.resolveApproval.reset()
        }
      })
    },
    confirmResolve (data) {
      this.$confirm('确定要提交此审批吗？', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.resolveDialog.submitting = true
        this.$ajax({
          url: '/workspace/manager/flow/record/todo/process/resolve',
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          data: this.$extend({
            id: this.rowData.id
          }, data)
        }).then(response => {
          if (response.data) {
            switch (response.data['responseCode']) {
              case 'SUCCESS': {
                this.$message.success(response.data['message'] || '审批提交成功')
                this.resolveDialog.visible = false
                this.$emit('hidePopoverAndRefresh')
                break
              }
              default: {
                this.$message.warning(response.data['message'])
                break
              }
            }
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.resolveDialog.submitting = false
        })
      }).catch(() => {})
    }
  },
  mounted () {
    this.loadDetailInfo()
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadDetailInfo()
      }
    }
  }
}
</script>

<style lang="scss">
.flow-record-todo {
  height: 100%;
  width: 760px;
  max-width: 100%;

  .todo-main {
    height: 100%;
    display: flex;
    flex-direction: column;

    .todo-scroll {
      flex: 1;
      overflow: hidden;
    }

    .todo-content {
      padding: 12px;

      .todo-section {
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        overflow: hidden;

        &:last-child {
          margin-bottom: 0;
        }

        .section-header {
          padding: 12px 15px;
          border-bottom: 1px solid #ebeef5;
          display: flex;
          align-items: center;
          justify-content: space-between;

          .section-title {
            font-size: 13px;
            font-weight: 500;
          }

          .section-header-tags {
            display: flex;
            align-items: center;
            gap: 6px;
          }
        }

        .section-body {
          .empty-hint {
            text-align: center;
            color: #909399;
            font-size: 13px;
            padding: 16px 0;
          }
        }

        .section-body-info {
          padding: 12px 15px;

          .info-row {
            display: flex;
            align-items: center;
            font-size: 12px;
            padding: 5px 0;

            .info-label {
              flex: none;
              width: 70px;
              color: #909399;
            }

            .info-value {
              flex: 1;
              color: #303133;
              min-width: 0;
            }
          }
        }

        .section-body-history {
          padding: 16px 20px;
        }
      }
    }

    .todo-actions {
      flex: none;
      min-height: 54px;
      padding: 10px 15px;
      background-color: #fff;
      border-top: 1px solid #ebeef5;

      .flow-ended-text {
        display: flex;
        align-items: baseline;
        font-size: 12px;
        color: #909399;

        i {
          margin-right: 4px;
          font-size: 13px;
        }
      }
    }
  }
}

.flow-record-todo-dialog {
  .dialog-form-content {
    padding: 15px 20px;
  }

  .member-select-area {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .transfer-member-items,
    .delegate-member-items,
    .parallel-multi-member-items {
      margin-top: 0 !important;
    }

    .transfer-member-placeholder,
    .delegate-member-placeholder,
    .parallel-multi-member-placeholder {
      font-size: 13px;
      color: #999;
    }
  }

  .sign-main-wrap {
    .sign-body {
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      overflow: hidden;
    }

    .sign-action {
      margin-top: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .el-radio-group {
        .el-radio-button .el-radio-button__inner {
          padding: 6px 10px;
        }

        .el-radio-button:first-child .el-radio-button__inner {
          border-radius: 30px 0 0 30px;
        }

        .el-radio-button:last-child .el-radio-button__inner {
          border-radius: 0 30px 30px 0;
        }
      }
    }
  }

  .dialog-footer {
    text-align: right;
  }
}
</style>
